// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SupportSnackbar } from '@/components/support-snackbar';
import { FormCustomerSearch } from '@/customer/components/form-customer-search';
import { getNewCompanyURL } from '@/customer/utilities/get-new-customer-url';
import { Project } from '@/project/api/types';
import { useUpdateProject } from '@/project/hooks/use-update-project';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  customer: z.object({ id: z.string(), name: z.string() }).nullable(),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  isOpen: boolean;
  project: Project;
  onClose: () => void;
}

export function ProjectCustomerDialog(props: Props) {
  const updateProjectMutation = useUpdateProject(props.project.key, {
    onSuccess: () => {
      props.onClose();
    },
  });

  const { control, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { customer: props.project.customer },
  });

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    updateProjectMutation.mutate({ customerId: data.customer?.id ?? null });
  };

  const handleCreateCustomer = (name: string) => {
    const url = getNewCompanyURL(name, true, false);

    window.open(url, '_blank');
  };

  const close = () => {
    props.onClose();
  };

  return (
    <>
      <SupportSnackbar
        isOpen={updateProjectMutation.isError}
        message="An error occurred while updating the project."
        onClose={updateProjectMutation.reset}
      />
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.isOpen}
        onClose={close}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <DialogTitle>Edit customer</DialogTitle>
        <DialogContent dividers>
          <FormCustomerSearch
            name="customer"
            label="Customer"
            control={control}
            disabled={updateProjectMutation.isPending}
            onCreate={handleCreateCustomer}
          />
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={close}
            disabled={updateProjectMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            size="small"
            type="submit"
            color="secondary"
            variant="contained"
            disabled={updateProjectMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
